import type { AccountFeature } from "@/types/model/User";
import type {
  FeatureAccessConfig,
  FeatureVisibility,
  SelfServicePlanType,
  UserFeatureContext,
} from "./featureAccess.types";
import { PLAN_VIEW_CONFIG } from "@/utilities/planHelpers";
import { VIEWS } from "../routes";
import { PLAN_MODE } from "@/utilities/constants";

export const FT_NAME = Object.freeze({
  // OVERVIEW
  OVERVIEW: "home",
  // PERFORMANCE
  PERFORMANCE_VIEW: "performance",
  // REPORTS
  REPORTS: "reports",
  // CHANNELS
  // GOOGLE
  GOOGLE: "google_ppc",
  GADS: {
    SHOPPING: "shopping",
    PMAX: "pmax",
    GLOBAL_INTELLIGENCE: "gads_global_intelligence",
  },
  // META
  META: "facebook_ads",
  // MICROSOFT
  MICROSOFT: "microsoft_ads",
  // TIKTOK
  TIKTOK: "tiktok_ads",
  // TABOOLA
  TABOOLA: "taboola",
  // REDDIT
  REDDIT: "reddit",
  // SETUP
  SENSOR_JS: "sensor_js",
  SENSOR_FIREWALL: "sensor_firewall",
  SENSOR_S2S: "sensor_s2s",
  HUBSPOT: "hubspot",
  BENCHMARK: "benchmark",
  AI_ASSISTANT: "ai_assistant",
  LEADS: "leads",
});

// Map view names to feature names for certain views
const mapViewToFeature = (viewName: string): string => {
  // Google sub-views map to main Google feature
  if (
    viewName === VIEWS.GOOGLE.SEARCH_DISPLAY ||
    viewName === VIEWS.GOOGLE.SHOPPING ||
    viewName === VIEWS.GOOGLE.PMAX_DEMANDGEN ||
    viewName === VIEWS.GOOGLE.ACCOUNT_SETTINGS
  ) {
    return FT_NAME.GOOGLE;
  }

  // Sensor Firewall sub-views
  if (
    viewName === VIEWS.SENSOR_FIREWALL.CAMPAIGNS ||
    viewName === VIEWS.SENSOR_FIREWALL.OFFERS ||
    viewName === VIEWS.SENSOR_FIREWALL.TRAFFIC_SOURCES ||
    viewName === VIEWS.SECURITY.APK ||
    viewName === VIEWS.SECURITY.DOMAIN_EXCLUSIONS ||
    viewName === VIEWS.SECURITY.IP_SETTINGS ||
    viewName === VIEWS.SECURITY.JS_SETTINGS ||
    viewName === VIEWS.SECURITY.POSTBACK
  ) {
    return FT_NAME.SENSOR_FIREWALL;
  }

  if (viewName === VIEWS.TIKTOK_INTEGRATION) {
    return FT_NAME.TIKTOK;
  }

  if (viewName === VIEWS.META_INTEGRATION) {
    return FT_NAME.META;
  }

  if (viewName === VIEWS.MICROSOFT_INTEGRATION) {
    return FT_NAME.MICROSOFT;
  }

  if (viewName === VIEWS.TABOOLA_INTEGRATION) {
    return FT_NAME.TABOOLA;
  }

  if (viewName === VIEWS.REDDIT_INTEGRATION) {
    return FT_NAME.REDDIT;
  }

  if (viewName === VIEWS.SETUP_CENTER) {
    return FT_NAME.SENSOR_JS;
  }

  return viewName;
};

// Resolve access for demo accounts
const resolveDemoAccess = (
  viewName: string,
  accountFeatures: AccountFeature[] | undefined
): FeatureAccessConfig => {
  const activeModules = accountFeatures?.map((f) => f.name) ?? [];

  // Demo accounts: some features are always visible
  const alwaysVisibleInDemo = [
    VIEWS.ANALYTICS,
    VIEWS.FORENSICS,
    VIEWS.CONVERSIONS,
    VIEWS.ALERTS,
    VIEWS.GOOGLE.SEARCH_DISPLAY,
    VIEWS.GOOGLE.SHOPPING,
    VIEWS.GOOGLE.PMAX_DEMANDGEN,
    VIEWS.GOOGLE.ACCOUNT_SETTINGS,
    VIEWS.ACCOUNT.SETTINGS,
    VIEWS.ACCOUNT.USERS,
    VIEWS.ACCOUNT.ACCESS_SETTINGS,
    VIEWS.ACCOUNT.REFERRAL,
    VIEWS.META_INTEGRATION,
    VIEWS.MICROSOFT_INTEGRATION,
    VIEWS.TIKTOK_INTEGRATION,
    VIEWS.TABOOLA,
    VIEWS.TABOOLA_INTEGRATION,
    VIEWS.REDDIT,
    VIEWS.REDDIT_INTEGRATION,
    VIEWS.SETUP_CENTER,
    VIEWS.INTEGRATIONS_HOME,
    VIEWS.CHANNELS_CENTER,
    VIEWS.LEADS,
  ];

  if (
    alwaysVisibleInDemo.includes(viewName) ||
    activeModules.includes(viewName)
  ) {
    return {
      visibility: "visible",
      showGetFeature: false,
    };
  }

  // Default: hidden
  return {
    visibility: "hidden",
    showGetFeature: false,
  };
};

// Resolve access for selfservice plans
const resolveSelfServiceAccess = (
  viewName: string,
  planType: SelfServicePlanType,
  accountFeatures: AccountFeature[] | undefined
): FeatureAccessConfig => {
  const activeModules = accountFeatures?.map((f) => f.name) ?? [];
  const planConfig = PLAN_VIEW_CONFIG[planType];

  // Check for explicit config first; if not found and it's a subview (dotted key),
  // fall back to the parent view's config
  let viewConfig: FeatureVisibility | undefined = planConfig?.[viewName];
  if (!viewConfig && viewName.includes(".")) {
    const parentView = viewName.split(".")[0] ?? "";
    viewConfig = planConfig?.[parentView];
  }

  if (viewConfig) {
    // View is explicitly configured in plan
    switch (viewConfig) {
      case "visible":
        return {
          visibility: "visible",
          showGetFeature: false,
        };

      case "locked":
        return {
          visibility: "locked",
          showGetFeature: true,
        };

      case "hidden":
        return {
          visibility: "hidden",
          showGetFeature: false,
        };
    }
  }

  // View not in plan config - check if it's in activeModules
  const isActive = activeModules.includes(viewName);
  return {
    visibility: isActive ? "visible" : "hidden",
    showGetFeature: false,
  };
};

// Resolve access for AppSumo plans
const resolveAppSumoAccess = (
  viewName: string,
  accountFeatures: AccountFeature[] | undefined
): FeatureAccessConfig => {
  const activeModules = accountFeatures?.map((f) => f.name) ?? [];

  // AppSumo: always hide sensor-only views
  const hiddenViews = [
    FT_NAME.SENSOR_FIREWALL,
    FT_NAME.SENSOR_S2S,
  ];

  if (
    hiddenViews.includes(
      viewName as "sensor_firewall" | "sensor_s2s"
    )
  ) {
    return {
      visibility: "hidden",
      showGetFeature: false,
    };
  }

  // controllable views through account features
  const accountFeatureControllableViews = [
    FT_NAME.GOOGLE,
    FT_NAME.META,
    FT_NAME.MICROSOFT,
    FT_NAME.TIKTOK,
    FT_NAME.TABOOLA,
    FT_NAME.REDDIT,
    FT_NAME.HUBSPOT,
    FT_NAME.BENCHMARK,
    FT_NAME.AI_ASSISTANT,
    FT_NAME.OVERVIEW,
    FT_NAME.LEADS,
    FT_NAME.REPORTS,
  ];

  // If viewName is not in accountFeatures, hide it
  const mappedFeature = mapViewToFeature(viewName);
  if (
    accountFeatureControllableViews.includes(
      viewName as
        | "home"
        | "google_ppc"
        | "facebook_ads"
        | "microsoft_ads"
        | "tiktok_ads"
        | "taboola"
        | "reddit"
        | "hubspot"
        | "benchmark"
        | "ai_assistant"
        | "reports"
        | "leads"
    ) &&
    !activeModules.includes(mappedFeature)
  ) {
    return {
      visibility: "hidden",
      showGetFeature: false,
    };
  }

  return {
    visibility: "visible",
    showGetFeature: false,
  };
};

// Resolve access for managed/field plans
const resolveManagedAccess = (
  viewName: string,
  accountFeatures: AccountFeature[] | undefined
): FeatureAccessConfig => {
  const mappedFeature = mapViewToFeature(viewName);
  const activeModules = accountFeatures?.map((f) => f.name) ?? [];
  // Views tha can show GetFeature component even if visible
  const getFeatureViewsList: string[] = [
    VIEWS.META,
    VIEWS.GOOGLE.MAIN,
    VIEWS.GOOGLE.GLOBAL_INTELLIGENCE,
    VIEWS.HUBSPOT,
    VIEWS.TIKTOK,
    VIEWS.TABOOLA,
    VIEWS.REDDIT,
    VIEWS.MICROSOFT,
    VIEWS.OVERVIEW,
    VIEWS.LEADS,
  ];

  // Managed accounts: some features are always visible
  const alwaysVisibleInManaged = [
    VIEWS.ANALYTICS,
    VIEWS.FORENSICS,
    VIEWS.CONVERSIONS,
    VIEWS.ALERTS,
    VIEWS.ACCOUNT.SETTINGS,
    VIEWS.ACCOUNT.USERS,
    VIEWS.ACCOUNT.ACCESS_SETTINGS,
    VIEWS.ACCOUNT.REFERRAL,
    VIEWS.INTEGRATIONS_HOME,
    VIEWS.ACCOUNT.DEVELOPERS_SETTINGS,
    VIEWS.CHANNELS_CENTER,
  ];

  const isChannelsCenterVisible =
    viewName === VIEWS.CHANNELS_CENTER &&
    (activeModules.includes(FT_NAME.GOOGLE) ||
      activeModules.includes(FT_NAME.META) ||
      activeModules.includes(FT_NAME.MICROSOFT) ||
      activeModules.includes(FT_NAME.TIKTOK) ||
      activeModules.includes(FT_NAME.TABOOLA) ||
      activeModules.includes(FT_NAME.REDDIT));

  if (
    alwaysVisibleInManaged.includes(mappedFeature) ||
    activeModules.includes(mappedFeature) ||
    isChannelsCenterVisible ||
    (viewName === VIEWS.ACCOUNT.LABELS &&
      activeModules.includes(FT_NAME.SENSOR_FIREWALL))
  ) {
    return {
      visibility: "visible",
      showGetFeature: false,
    };
  }

  // Get Feature case for certain views
  if (getFeatureViewsList.includes(viewName)) {
    return {
      visibility: "visible",
      showGetFeature: true,
    };
  }

  const canUserSeePerformanceSection = [
    FT_NAME.META,
    FT_NAME.MICROSOFT,
    FT_NAME.TABOOLA,
    FT_NAME.REDDIT,
    FT_NAME.HUBSPOT,
    FT_NAME.TIKTOK,
    FT_NAME.GOOGLE,
  ].some((featureName) => {
    const access = activeModules.includes(featureName);
    const accessFirewall = activeModules.includes(FT_NAME.SENSOR_FIREWALL);
    return access && !accessFirewall;
  });

  if (viewName === VIEWS.PERFORMANCE && !canUserSeePerformanceSection) {
    return {
      visibility: "hidden",
      showGetFeature: false,
    };
  }

  if (viewName === VIEWS.PERFORMANCE && canUserSeePerformanceSection) {
    return {
      visibility: "visible",
      showGetFeature: false,
    };
  }

  // Default: hidden
  return {
    visibility: "hidden",
    showGetFeature: false,
  };
};

// Main resolver function - determines feature access for a given view
export const resolveAccess = (
  viewName: string,
  context: UserFeatureContext
): FeatureAccessConfig => {
  const { accountFeatures, planType = "", planMode = "" } = context;

  // Handle demo accounts
  if (planMode === PLAN_MODE.DEMO) {
    return resolveDemoAccess(viewName, accountFeatures);
  }

  // Handle selfservice plans (free_trial, lite, pro, max)
  if (planMode === PLAN_MODE.FREE || planMode === PLAN_MODE.SELFSERVICE) {
    return resolveSelfServiceAccess(
      viewName,
      planType as SelfServicePlanType,
      accountFeatures
    );
  }

  // Handle appsumo plans
  if (planMode === PLAN_MODE.APPSUMO) {
    return resolveAppSumoAccess(viewName, accountFeatures);
  }

  // Handle managed/field plans (default)
  if (planMode === PLAN_MODE.MANAGED) {
    return resolveManagedAccess(viewName, accountFeatures);
  }

  // Default: hidden
  return {
    visibility: "hidden",
    showGetFeature: false,
  };
};

export const featureAccessResolver = {
  resolveAccess,
};
